import { isMp } from '@uni-helper/uni-env';
/**
 * by 菲鸽 on 2025-08-19
 * 路由拦截，通常也是登录拦截
 * 黑、白名单的配置，请看 config.ts 文件， EXCLUDE_LOGIN_PATH_LIST
 */
import { isWorkbenchMode } from '@/project';
import { useTokenStore } from '@/store/modules/token';
import { isPageTabbar, tabbarStore } from '@/tabbar/store';
import { getAllPages, getLastPage, HOME_PAGE, parseUrlToObj } from '@/utils';
import { trackPageEnd, trackPageStart } from '@/utils/analytics';
import { sendVisit } from '@/utils/logReporter';
import { msg } from '@/utils/modal';
import { goHome } from './back';
import {
  EXCLUDE_LOGIN_PATH_LIST,
  isNeedLoginMode,
  LOGIN_PAGE,
  LOGIN_PAGE_ENABLE_IN_MP,
  OLD_ROUTE_MAP,
  UPLOAD_DISABLED_PATH_PREFIXES,
  UPLOAD_DISABLED_TIP,
} from './config';

export const FG_LOG_ENABLE = false;

export function judgeIsExcludePath(path: string) {
  const isDev = import.meta.env.DEV;
  if (!isDev) {
    return EXCLUDE_LOGIN_PATH_LIST.includes(path);
  }
  const allExcludeLoginPages = getAllPages('excludeLoginPath'); // dev 环境下，需要每次都重新获取，否则新配置就不会生效
  return EXCLUDE_LOGIN_PATH_LIST.includes(path) || (isDev && allExcludeLoginPages.some(page => page.path === path));
}

// 缓存 needLogin 页面路径，生产环境避免重复计算
let needLoginPathCache: string[] | null = null;

/**
 * 获取所有配置了 needLogin: true 的页面路径
 * 用于黑名单策略下识别需要登录的页面
 */
function getNeedLoginPaths(): string[] {
  if (!import.meta.env.DEV && needLoginPathCache) {
    return needLoginPathCache;
  }
  needLoginPathCache = getAllPages('needLogin').map(page => page.path);
  return needLoginPathCache;
}

export const navigateToInterceptor = {
  // 注意，这里的url是 '/' 开头的，如 '/pages/index/index'，跟 'pages.json' 里面的 path 不同
  // 增加对相对路径的处理，BY 网友 @ideal
  invoke({ url, query }: { url: string, query?: Record<string, string> }) {
    if (url === undefined) {
      return;
    }
    let { path, query: _query } = parseUrlToObj(url);

    FG_LOG_ENABLE && console.log('\n\n路由拦截器:-------------------------------------');
    FG_LOG_ENABLE && console.log('路由拦截器 1: url->', url, ', query ->', query);
    const myQuery = { ..._query, ...query };
    // /pages/route-interceptor/index?name=feige&age=30
    FG_LOG_ENABLE && console.log('路由拦截器 2: path->', path, ', _query ->', _query);
    FG_LOG_ENABLE && console.log('路由拦截器 3: myQuery ->', myQuery);

    // 处理相对路径
    if (!path.startsWith('/')) {
      const currentPath = getLastPage()?.route || '';
      const normalizedCurrentPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
      const baseDir = normalizedCurrentPath.substring(0, normalizedCurrentPath.lastIndexOf('/'));
      path = `${baseDir}/${path}`;
    }

    // 兼容 Vue2 旧路由映射
    if (OLD_ROUTE_MAP[path]) {
      console.warn(`[路由拦截] Vue2 旧路由 ${path} -> ${OLD_ROUTE_MAP[path]}`);
      // 执行重定向到新路径，并保留原始查询参数
      let newUrl = OLD_ROUTE_MAP[path];
      if (Object.keys(myQuery).length > 0) {
        const queryStr = Object.entries(myQuery)
          .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
          .join('&');
        newUrl = `${newUrl}?${queryStr}`;
      }
      uni.navigateTo({ url: newUrl });
      return false;
    }

    // iOS（工作台模式）功能裁剪：上货功能整体下线，页面已不打包、通知已过滤；
    // 此处为纯防呆兜底（陈旧链接/误加入口），导航前给出提示
    if (isWorkbenchMode() && UPLOAD_DISABLED_PATH_PREFIXES.some(prefix => path.startsWith(prefix))) {
      msg(UPLOAD_DISABLED_TIP, 'info');
      return false; // 阻断导航，停留当前页
    }

    // // 处理路由不存在的情况
    // if (path !== '/' && !getAllPages().some(page => page.path === path)) {
    //   console.warn('路由不存在:', path)
    //   return false // 明确表示阻止路由继续执行
    // }

    // // 插件页面
    // if (url.startsWith('plugin://')) {
    //   FG_LOG_ENABLE && console.log('路由拦截器 4: plugin:// 路径 ==>', url)
    //   path = url
    // }

    // 处理直接进入路由非首页时，tabbarIndex 不正确的问题
    tabbarStore.setAutoCurIdx(path);

    // App 端页面 PV 上报（百度移动统计原生插件不支持自动全埋点，需手动记录）
    // #ifdef APP-PLUS
    const _pages = getCurrentPages();
    if (_pages.length > 0) {
      const _oldPage = _pages[_pages.length - 1] as any;
      if (_oldPage?.route) trackPageEnd(_oldPage.route);
    }
    trackPageStart(path.replace(/^\//, ''));
    // #endif

    // 小程序端页面 PV 上报（uni-app 不使用全局 Page()，SDK 自动包裹无效）
    // #ifdef MP-WEIXIN
    // setTimeout(0) 确保在新页面入栈后执行，wx.mtj.pageEvent.onShow() 内部使用 getCurrentPages()
    setTimeout(() => {
      trackPageStart(path.replace(/^\//, ''));
    }, 0);
    // #endif

    // H5端：页面访问上报（延迟到导航完成后执行，记录新页面 URL 和来源页）
    // #ifdef H5
    const _h5Referrer = window.location.href;
    setTimeout(() => {
      sendVisit({ referrer: _h5Referrer });
    }, 0);
    // #endif

    // 小程序里面使用平台自带的登录，则不走下面的逻辑
    if (isMp && !LOGIN_PAGE_ENABLE_IN_MP) {
      return true; // 明确表示允许路由继续执行
    }

    const tokenStore = useTokenStore();
    // 先更新 nowTime 确保过期判断使用当前时间，避免持久化的旧 nowTime 导致误判
    tokenStore.updateNowTime();
    FG_LOG_ENABLE && console.log('tokenStore.hasLogin:', tokenStore.hasLogin);

    // 退出登录进行中，跳过登录检查，避免导航竞态（redirectTo 与 switchTab 冲突）
    if (tokenStore.isLoggingOut) {
      FG_LOG_ENABLE && console.log('路由拦截器: 退出登录中，直接放行');
      return true;
    }

    // 不管黑白名单，登录了就直接去吧（但是当前不能是登录页）
    if (tokenStore.hasLogin) {
      if (path !== LOGIN_PAGE) {
        return true; // 明确表示允许路由继续执行
      }
      else {
        console.log('已经登录，但是还在登录页', myQuery.redirect);
        const url = myQuery.redirect || HOME_PAGE;
        if (isPageTabbar(url)) {
          uni.switchTab({ url });
        }
        else {
          uni.navigateTo({ url });
        }
        return false; // 明确表示阻止原路由继续执行
      }
    }
    let fullPath = path;

    if (Object.keys(myQuery).length) {
      fullPath += `?${Object.keys(myQuery).map(key => `${key}=${myQuery[key]}`).join('&')}`;
    }
    const redirectUrl = `${LOGIN_PAGE}?redirect=${encodeURIComponent(fullPath)}`;

    // #region 1/2 默认需要登录的情况(白名单策略) ---------------------------
    if (isNeedLoginMode) {
      // 需要登录里面的 EXCLUDE_LOGIN_PATH_LIST 表示白名单，可以直接通过
      if (judgeIsExcludePath(path)) {
        return true; // 明确表示允许路由继续执行
      }
      // 否则需要重定向到登录页
      else {
        if (path === LOGIN_PAGE) {
          return true; // 明确表示允许路由继续执行
        }
        FG_LOG_ENABLE && console.log('1 isNeedLogin(白名单策略) redirectUrl:', redirectUrl);
        // 工作台模式页面栈只保留「登录页或 tabbar 页」：用 reLaunch 清掉内容页栈再进登录，
        // 避免 navigateTo 造成登录页下压着未登录内容页的混合栈
        uni.reLaunch({ url: redirectUrl });
        return false; // 明确表示阻止原路由继续执行
      }
    }
    // #endregion 1/2 默认需要登录的情况(白名单策略) ---------------------------

    // #region 2/2 默认不需要登录的情况(黑名单策略) ---------------------------
    else {
      // 不需要登录里面的 EXCLUDE_LOGIN_PATH_LIST 表示黑名单，需要重定向到登录页
      if (judgeIsExcludePath(path)) {
        FG_LOG_ENABLE && console.log('2 isNeedLogin(黑名单策略) redirectUrl:', redirectUrl);
        uni.navigateTo({ url: redirectUrl });
        return false; // 修改为false，阻止原路由继续执行
      }

      // 额外检查 needLogin 配置（definePage 中配置的 needLogin: true）
      if (getNeedLoginPaths().includes(path)) {
        FG_LOG_ENABLE && console.log('2 isNeedLogin(needLogin配置) redirectUrl:', redirectUrl);
        uni.navigateTo({ url: redirectUrl });
        return false; // 阻止原路由继续执行
      }

      return true; // 明确表示允许路由继续执行
    }
    // #endregion 2/2 默认不需要登录的情况(黑名单策略) ---------------------------
  },
};

/**
 * navigateBack 拦截：页面栈只有一个页面时（扫码、分享链接等深层链接直接进入内页）改为回首页
 *
 * uni-app 会先把 delta 钳制为 `getCurrentPages().length - 1`，页面栈为 1 时 delta 变成 0，
 * H5 端等价于 `history.go(0)`（刷新当前页），表现为「点返回回不到首页，一直在当前页刷新」。
 */
export const navigateBackInterceptor = {
  invoke(args: { delta?: number, from?: string }) {
    // App 端系统返回键会经由此 API（框架 backbuttonListener 调 uni.navigateBack({ from: 'backbutton' })），
    // 必须交还框架原生处理：首页走 isQuit 两次返回退出流程、深层链接页走 reLaunchEntryPage；
    // 若在此拦截（return false），会导致首页按系统返回键无任何响应（荣耀/华为真机实测复现，
    // vue2 与 chat-mobile 均未注册 navigateBack 拦截器故表现正常）
    if (args?.from === 'backbutton') {
      return true;
    }
    if (getCurrentPages().length > 1) {
      return true; // 页面栈正常，放行
    }
    FG_LOG_ENABLE && console.log('路由拦截器: navigateBack 页面栈不足，回首页兜底', args);
    goHome();
    return false; // 阻止原 navigateBack，避免退化成刷新当前页
  },
};

// 针对 chooseLocation 的特殊处理
export const chooseLocationInterceptor = {
  invoke(options: any) {
    // 直接放行 chooseLocation 调用
    FG_LOG_ENABLE && console.log('chooseLocation 调用，直接放行:', options);
    return true;
  },
};

export const routeInterceptor = {
  install() {
    uni.addInterceptor('navigateTo', navigateToInterceptor);
    uni.addInterceptor('reLaunch', navigateToInterceptor);
    uni.addInterceptor('redirectTo', navigateToInterceptor);
    uni.addInterceptor('switchTab', navigateToInterceptor);
    uni.addInterceptor('navigateBack', navigateBackInterceptor);

    // 添加 chooseLocation 的拦截器，确保直接放行
    uni.addInterceptor('chooseLocation', chooseLocationInterceptor);
  },
};
